import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit2, Eye, FileDown, Calendar, User, Tag, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

interface SessionNote {
  id: string;
  title: string;
  content: string;
  note_type: string;
  tags: string[];
  is_private: boolean;
  client_id: string | null;
  booking_id: string | null;
  created_at: string;
  updated_at: string;
  client?: {
    first_name: string;
    last_name: string;
  };
  booking?: {
    start_time: string;
    service_id: string;
  };
}

interface SessionNotesTabProps {
  userId: string;
}

export const SessionNotesTab: React.FC<SessionNotesTabProps> = ({ userId }) => {
  const [notes, setNotes] = useState<SessionNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNoteType, setSelectedNoteType] = useState<string>('all');
  const [editingNote, setEditingNote] = useState<SessionNote | null>(null);
  const [viewingNote, setViewingNote] = useState<SessionNote | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    note_type: 'general',
    tags: [] as string[],
    is_private: false,
    client_id: null as string | null,
    booking_id: null as string | null
  });
  const { toast } = useToast();

  const noteTypes = [
    { value: 'general', label: 'General' },
    { value: 'session', label: 'Session Notes' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'treatment_plan', label: 'Treatment Plan' },
    { value: 'progress', label: 'Progress Notes' },
    { value: 'intake', label: 'Intake Notes' }
  ];

  useEffect(() => {
    fetchNotes();
  }, [userId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('medical_notes')
        .select('*')
        .eq('practitioner_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching session notes:', error);
      toast({
        title: "Error",
        description: "Failed to fetch session notes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const createNote = async () => {
    try {
      const { data, error } = await supabase
        .from('medical_notes')
        .insert([{
          ...newNote,
          practitioner_id: userId
        }])
        .select()
        .single();

      if (error) throw error;

      setNotes(prev => [data, ...prev]);
      setNewNote({
        title: '',
        content: '',
        note_type: 'general',
        tags: [],
        is_private: false,
        client_id: null,
        booking_id: null
      });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "Medical note created successfully"
      });
    } catch (error) {
      console.error('Error creating note:', error);
      toast({
        title: "Error",
        description: "Failed to create medical note",
        variant: "destructive"
      });
    }
  };

  const updateNote = async () => {
    if (!editingNote) return;

    try {
      const { data, error } = await supabase
        .from('medical_notes')
        .update({
          title: editingNote.title,
          content: editingNote.content,
          note_type: editingNote.note_type,
          tags: editingNote.tags,
          is_private: editingNote.is_private
        })
        .eq('id', editingNote.id)
        .select()
        .single();

      if (error) throw error;

      setNotes(prev => prev.map(note => 
        note.id === editingNote.id ? { ...note, ...data } : note
      ));
      setEditingNote(null);
      toast({
        title: "Success",
        description: "Medical note updated successfully"
      });
    } catch (error) {
      console.error('Error updating note:', error);
      toast({
        title: "Error",
        description: "Failed to update medical note",
        variant: "destructive"
      });
    }
  };

  const exportNote = (note: SessionNote) => {
    const content = `Session Note - ${note.title}
Created: ${format(new Date(note.created_at), 'PPP')}
Type: ${note.note_type}
${note.client ? `Client: ${note.client.first_name} ${note.client.last_name}` : ''}
${note.tags.length > 0 ? `Tags: ${note.tags.join(', ')}` : ''}

Content:
${note.content}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-note-${note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportNoteAsDocx = async (note: SessionNote) => {
    try {
      // Create document sections
      const children: (Paragraph)[] = [
        new Paragraph({
          text: `Session Note - ${note.title}`,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: "Created: ",
              bold: true
            }),
            new TextRun({
              text: format(new Date(note.created_at), 'PPP')
            })
          ],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: "Type: ",
              bold: true
            }),
            new TextRun({
              text: note.note_type.replace('_', ' ').toUpperCase()
            })
          ],
          spacing: { after: 100 }
        })
      ];

      // Add client info if available
      if (note.client) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Client: ",
                bold: true
              }),
              new TextRun({
                text: `${note.client.first_name} ${note.client.last_name}`
              })
            ],
            spacing: { after: 100 }
          })
        );
      }

      // Add tags if available
      if (note.tags.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Tags: ",
                bold: true
              }),
              new TextRun({
                text: note.tags.join(', ')
              })
            ],
            spacing: { after: 200 }
          })
        );
      }

      // Add content header
      children.push(
        new Paragraph({
          text: "Content:",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      // Split content by lines and create paragraphs
      const contentLines = note.content.split('\n');
      contentLines.forEach(line => {
        children.push(
          new Paragraph({
            text: line || " ", // Empty line becomes space to preserve formatting
            spacing: { after: 100 }
          })
        );
      });

      // Create the document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });

      // Generate and save the document
      const blob = await Packer.toBlob(doc);
      const fileName = `medical-note-${note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
      saveAs(blob, fileName);

      toast({
        title: "Download Started",
        description: "Your medical note is being downloaded as a Word document."
      });
    } catch (error) {
      console.error('Error exporting note as DOCX:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export medical note as Word document. Please try again.",
        variant: "destructive"
      });
    }
  };

  const filteredNotes = notes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = selectedNoteType === 'all' || note.note_type === selectedNoteType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading medical notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Medical Notes</h2>
          <p className="text-muted-foreground">Manage your clinical notes and documentation</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Medical Note</DialogTitle>
              <DialogDescription>
                Create a new medical note for your records
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={newNote.title}
                  onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Note title"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={newNote.note_type} onValueChange={(value) => setNewNote(prev => ({ ...prev, note_type: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {noteTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Content</label>
                <Textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter your medical notes here..."
                  rows={6}
                />
              </div>
              <div className="flex gap-4">
                <Button onClick={createNote} disabled={!newNote.title || !newNote.content}>
                  Create Note
                </Button>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search notes, content, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedNoteType} onValueChange={setSelectedNoteType}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {noteTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Notes List */}
      <div className="grid gap-4">
        {filteredNotes.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">No medical notes found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm || selectedNoteType !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first medical note to get started'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredNotes.map((note) => (
            <Card key={note.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{note.title}</CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(note.created_at), 'MMM d, yyyy')}
                      </span>
                      {note.client && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {note.client.first_name} {note.client.last_name}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {noteTypes.find(t => t.value === note.note_type)?.label}
                    </Badge>
                    {note.is_private && (
                      <Badge variant="outline">Private</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {note.tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-muted-foreground" />
                      {note.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {note.content.substring(0, 200)}
                    {note.content.length > 200 && '...'}
                  </p>
                  
                  <Separator />
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Updated {format(new Date(note.updated_at), 'MMM d, yyyy')}
                    </span>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => setViewingNote(note)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{viewingNote?.title}</DialogTitle>
                            <DialogDescription>
                              Created on {viewingNote && format(new Date(viewingNote.created_at), 'PPP')}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="whitespace-pre-wrap text-sm">
                              {viewingNote?.content}
                            </div>
                            {viewingNote?.tags.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Tag className="w-4 h-4 text-muted-foreground" />
                                {viewingNote.tags.map((tag, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => setEditingNote(note)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Edit Medical Note</DialogTitle>
                          </DialogHeader>
                          {editingNote && (
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium">Title</label>
                                <Input
                                  value={editingNote.title}
                                  onChange={(e) => setEditingNote(prev => prev ? { ...prev, title: e.target.value } : null)}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium">Content</label>
                                <Textarea
                                  value={editingNote.content}
                                  onChange={(e) => setEditingNote(prev => prev ? { ...prev, content: e.target.value } : null)}
                                  rows={6}
                                />
                              </div>
                              <div className="flex gap-4">
                                <Button onClick={updateNote}>
                                  Update Note
                                </Button>
                                <Button variant="outline" onClick={() => setEditingNote(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => exportNote(note)}
                          title="Download as Text"
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => exportNoteAsDocx(note)}
                          title="Download as Word Document"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <FileDown className="w-4 h-4" />
                          <span className="text-xs ml-1">DOCX</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};